import { Component, signal, effect, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthState } from './state/store/auth/auth.reducer';
import { AuthActions } from './state/store/auth/auth.actions';
import { HeaderComponent } from './layout/header/header.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { FooterComponent } from './layout/footer/footer.component';
import {
  selectToken,
  selectLoading as AuthLoading,
} from './state/store/auth/auth.selectors';
import {
  selectLoading as ParentLoading,
  selectParent,
} from './state/store/parent/parent.selectors';
import { selectLoading as ClientLoading } from './state/store/client/client.selectors';
import { selectLoading as FilterLoading } from './state/store/filter/filter.selectors';
import { selectLoading as LocationLoading } from './state/store/location/location.selectors';
import { selectLoading as ProgramLoading } from './state/store/program/program.selectors';
import { selectLoading as DispositionLoading } from './state/store/disposition/disposition.selectors';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { TranslateService } from '@ngx-translate/core';
import { selectRolePermissionLoading } from './state/store/roles/role-permission.selectors';
import { selectUserLoading } from './state/store/user/user.selectors';
import { Location } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    SidebarComponent,
    FooterComponent,
    LoaderComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'project_forge';

  tokenSignal = this.store.selectSignal(selectToken);
  showLoader = signal(false);

  authLoaderSignal = this.store.selectSignal(AuthLoading);
  parentLoaderSignal = this.store.selectSignal(ParentLoading);
  roleLoaderSignal = this.store.selectSignal(selectRolePermissionLoading);
  clientLoaderSignal = this.store.selectSignal(ClientLoading);
  locationLoaderSignal = this.store.selectSignal(LocationLoading);
  selectParent = this.store.selectSignal(selectParent);
  filterLoaderSignal = this.store.selectSignal(FilterLoading);
  dispositionLoaderSignal = this.store.selectSignal(DispositionLoading);
  userLoaderSignal = this.store.selectSignal(selectUserLoading);
  programLoaderSignal = this.store.selectSignal(ProgramLoading);
  authRoutes = [
    'login',
    'reset-password',
    'set-password',
    'forgot-password',
    'verify-account',
    'account-locked',
    'account-locked-temp',
  ];
  currentUrl = '';
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly location: Location,
    private readonly store: Store<{ auth: AuthState }>
  ) {
    // Set initial URL early so template logic (isAuthRoute) is correct before first change detection
    this.currentUrl = this.router.url;
  }

  loaderEffect = effect(() => {
    const showLoader =
      this.authLoaderSignal() ||
      this.parentLoaderSignal() ||
      this.roleLoaderSignal() ||
      this.clientLoaderSignal() ||
      this.locationLoaderSignal() ||
      this.dispositionLoaderSignal() ||
      this.userLoaderSignal() ||
      this.programLoaderSignal() ||
      this.filterLoaderSignal();
    this.showLoader.set(showLoader);
  });

  ngOnInit(): void {
    this.translate.setDefaultLang('en');

    // In case location.path differs (e.g., hash or direct deep link), ensure we have a normalized path
    this.currentUrl = this.location.path() || this.currentUrl;

    // Keep currentUrl updated on navigation changes
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(e => {
        this.currentUrl = e.urlAfterRedirects;
      });

    if (this.currentUrl.startsWith('/set-password/')) {
      const token = this.currentUrl.split('/set-password/')[1];
      this.store.dispatch(AuthActions.verifyResetToken({ token, isResetToken: false }));
    } else if (this.currentUrl.startsWith('/reset-password/')) {
      const token = this.currentUrl.split('/reset-password/')[1];
      this.store.dispatch(AuthActions.verifyResetToken({ token, isResetToken: true }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isAuthRoute(): boolean {
    // Normalize currentUrl: remove leading slash, ignore query/hash, take first segment
    const cleaned = this.currentUrl.replace(/^\//, '').split('?')[0].split('#')[0];
    const firstSegment = cleaned.split('/')[0];
    return this.authRoutes.includes(firstSegment);
  }
}
